/**
 * App.js
 * Main application component that sets up routing and handles game stages.
 * It initializes the socket connection and provides the main game interface.
 * Handles navigation between different game stages and components.
 */

// React imports
import { useEffect, useState, useContext } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";

// Socket imports
import { SocketContext } from "./context/SocketProvider";
import { useGameStage } from "./hooks/useGameStage";

// Constants
import { GAME_STAGES } from "./constants/gameStages";
import { useRoleBackground } from "./hooks/useRoleBackground";

// Page imports
import {
  HomePage,
  PickingImage,
  DescribeImage,
  GuesserGuess,
  SeeResults,
  WaitingForGuesser,
  WaitingForDescriber,
  GamemodeV2,
  WaitingForGamemode,
  ShowGamemode,
  PickingImageNormal,
  DescribeImageNormal,
  AIWaiting,
  Points,
  DataCollectionSettings,
} from "./Pages";

import { PausedOverlay, TaskBar, PlayerLeftNotification, GameEventPopup } from "./components";
import FullscreenPrompt from "./components/FullscreenPrompt";
import PlayerIdBadge from "./components/PlayerIdBadge";

import { useSettings } from "./context/SettingsContext";
import { LoadingProvider } from "./context/LoadingContext";
import { LanguageProvider } from "./context/LanguageContext";
import TimerLayout from "./components/TimerLayout";
import WriteSentenceNormal from "./Pages/Describer/WriteSentenceNormal";

function App() {
  const { imageCount, toggleImageCount } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [warning, setWarning] = useState("");
  const [gameStarted, setGameStarted] = useState(false);
  const { socket, role, gameStage, showPausedOverlay, setShowPausedOverlay } =
    useContext(SocketContext);
  const { updateStage } = useGameStage();
  const { bgStyle, className } = useRoleBackground(role);

  // Only allow role swapping when game hasn't started (can swap in waiting or ready state)
  const canSwapRoles = (gameStage === GAME_STAGES.READY || gameStage === GAME_STAGES.WAITING) && !gameStarted;

  // Check if current route is home page or pre-game page (no taskbar)
  const isHomePage = location.pathname === "/" || location.pathname === "/home";

  // print stage changes for debugging purposes
  useEffect(() => {
    console.log(`🔄 gameStage changed to: ${gameStage}`);
  }, [gameStage]);

  // Listen for game started events from other players
  useEffect(() => {
    if (socket) {
      const handleGameStarted = () => {
        console.log("🎮 Game started by another player");
        setGameStarted(true);
      };

      const handleGameReset = () => {
        console.log("🔄 Game reset");
        setGameStarted(false);
      };

      socket.on("game_started", handleGameStarted);
      socket.on("game_reset", handleGameReset);

      return () => {
        socket.off("game_started", handleGameStarted);
        socket.off("game_reset", handleGameReset);
      };
    }
  }, [socket]);

  // reset all images and text boxes when going to the home screen
  useEffect(() => {
    localStorage.removeItem("allImages");
    localStorage.removeItem("description");
  }, []);


  return (
    <LanguageProvider>
      <div className={`relative ${className}`} style={bgStyle}>
        {!isHomePage && (
          <TaskBar
            imageCount={imageCount}
            toggleImageCount={toggleImageCount}
            canSwapRoles={canSwapRoles}
          />
        )}
        <div 
          className={`${isHomePage ? 'h-full' : 'h-[calc(100vh-80px)]'} relative ${className}`} 
          style={bgStyle}
        >
          <LoadingProvider>
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                gameStage={gameStage}
                role={role}
                socket={socket}
                setGameStarted={setGameStarted}
                setWarning={setWarning}
                warning={warning}
                navigate={navigate}
                canSwapRoles={canSwapRoles}
              />
            }
          />

          <Route path="/datacollection" element={<DataCollectionSettings />} />

          <Route
            path="/home"
            element={
              <HomePage
                gameStage={gameStage}
                role={role}
                socket={socket}
                setGameStarted={setGameStarted}
                setWarning={setWarning}
                warning={warning}
                navigate={navigate}
                canSwapRoles={canSwapRoles}
              />
            }
          />

          <Route
            path="/waitingforguesser"
            element={
              <TimerLayout shouldStartTimer={false}>
                <WaitingForGuesser gameStage={gameStage} />
              </TimerLayout>
            }
          />
          <Route
            path="/waitingfordescriber"
            element={
              <TimerLayout shouldStartTimer={false}>
                <WaitingForDescriber gameStage={gameStage} />
              </TimerLayout>
            }
          />
          <Route
            path="/gamemode"
            element={<GamemodeV2 role={role} gameStage={gameStage} />}
          />

          <Route
            path="/waitingforgamemode"
            element={<WaitingForGamemode role={role} gameStage={gameStage} />}
          />
          <Route path="/showgamemode" element={<ShowGamemode />} />
          <Route
            path="/pickingimage"
            element={
              <TimerLayout shouldStartTimer={false}>
                {/* TODO: check it */}
                <PickingImage />
              </TimerLayout>
            }
          />
          <Route
            path="/describeimage"
            element={
              <TimerLayout shouldStartTimer={false}>
                <DescribeImage />
              </TimerLayout>
            }
          />
          <Route
            path="/writesentencenormal"
            element={
              <TimerLayout>
                <WriteSentenceNormal />
              </TimerLayout>
            }
          />
          <Route
            path="/pickingimagenormal"
            element={
              <TimerLayout>
                <PickingImageNormal />
              </TimerLayout>
            }
          />
          <Route
            path="/describeimagenormal"
            element={
              <TimerLayout>
                <DescribeImageNormal />
              </TimerLayout>
            }
          />
          <Route
            path="/guesserguess"
            element={
              <TimerLayout duration={60} shouldStartTimer={false}>
                <GuesserGuess />
              </TimerLayout>
            }
          />
          <Route path="/seeresults" element={<SeeResults />} />
          <Route path="/points" element={<Points />} />
          <Route path="/aiwaiting" element={<AIWaiting />} />
        </Routes>
          </LoadingProvider>
        </div>
        {showPausedOverlay && (
          <PausedOverlay/>
        )}
        
        {/* Player Left Notification Modal */}
        <PlayerLeftNotification />
        
        {/* Fullscreen Prompt for iPad */}
        <FullscreenPrompt />

        {/* Persistent Player ID badge — bottom-left when Data Collection is ON */}
        <PlayerIdBadge />

        {/* Global Popup for role-specific notifications */}
        <GameEventPopup />
      </div>
    </LanguageProvider>
  );
}

export default App;
